import { useState, useEffect, useRef } from 'react';
import { useParams } from '@reach/router';
import { Form, FormState, FormApi } from 'informed';
import { connect, useDispatch, useSelector } from 'react-redux';
import { QuoteWizardState, StoreState } from '../../../store';
import { useLazyQuery } from '@apollo/client';
// material
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { withStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import { Select, TileSelector } from '@calefy-inc/informedMaterial';
import {
  allBusinessTypes as allBusinessTypesQuery,
  GET_GENERAL_INFORMATION_FORM_FOR_BUSINESS,
} from '../../../queries';
import { BackButton, NextButton } from '../NavigationButtons';
import { hideSaveButton } from '../../../store/QuoteWizardState';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import { handleErrors, DisconnectionError } from '../../common/ErrorHandling';
import {
  useWarnBeforeUnloadWithAnswers,
  useSettings,
  useIsOpenAiUp,
} from '../../../hooks';
import { DataLossWarning } from './DataLossWarning';
import { StepWizardChildProps } from 'react-step-wizard';
import {
  businessOptionsMatcher,
  BUSINESS_NOT_FOUND_LABEL,
  generateHighlightedLabel,
  unmangleId,
} from './utility';
import bugsnag from '@bugsnag/js';
import { useLoadOrphanedAnswers } from '../../../hooks';

import { actions } from '../../../store/QuoteWizardState';
import {
  selectBusinessTypeThunk,
  setBusinessTypeData,
  setBusinessTypeFoundManually,
  gptBusinessType,
  setBusinessDescriptionRedux,
} from '../../../store/QuoteWizardState';
import {
  requestNewStatisticalSessionThunk,
  createBusinessSelectionInputThunk,
  createBusinessSelectionSelectedThunk,
} from '../../../store/analyticsStore';
import { useTileSelectorSettings } from '../../../hooks';
import { useWalnutGoogleAnalytics } from '../../../hooks';
import {
  GAEventNames,
  EventActions,
} from '../../../hooks/useWalnutGoogleAnalytics';
import { createPageLoadThunk } from '../../../store/analyticsStore';
import Tooltip from '@mui/material/Tooltip';

//types and classes
/* import { QuoteWizardForm } from '../classes'; */
import type { $TSFixMe, ArrayElement } from '@calefy-inc/utilityTypes';
import { BusinessType } from '../../../Typescript';
import { ProgramBuilderForm } from '../../FormManager/classes';
import type { Dispatch } from '../../../store';
import { BusinessTypeAlias } from '../../../Typescript/classes';
import {
  vagoSettings,
  VAGO_BUSINESS_INTERNAL_NAME,
} from '../../../WhiteLabelSettings/VagoAssure';
import { graphql } from '../../../gql';
import { useTheme } from '@mui/material/styles';
import HelpSharpIcon from '@mui/icons-material/HelpSharp';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';

const BUSINESS_SELECTION_DISPATCH_INTERVAL = 400; // time to wait after a key is pressed before sending off the record of the input
const GPT_BUSINESS_SELECTION_DISPATCH_INTERVAL = 600;

interface QuoteBusinessSelectionDisplayProps
  extends StepWizardChildProps,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  allBusinessTypes: Array<BusinessType | BusinessTypeAlias>;
  nextStep: $TSFixMe;
  nextFormStep: $TSFixMe;
  classes: $TSFixMe;
  loading: $TSFixMe;
  error: $TSFixMe;
}

/**
 * Component which allows the user to select the business type for the quote
 * @param allBusinessTypes - A list of all of the possible business types
 * @param selectedBusinessType - The selected business type
 * @param onBusinessSelection - the function to be called when a business is selected
 */
export function QuoteBusinessSelectionDisplay({
  allBusinessTypes,
  selectedBusinessType,
  selectedInsurType,
  onBusinessSelection,
  nextStep,
  classes = {},
  language,
  generalInformationFormLoaded,
  setAdditionalInformation,
  currentStep,
  totalSteps,
}: QuoteBusinessSelectionDisplayProps) {
  useWarnBeforeUnloadWithAnswers();
  useLoadOrphanedAnswers('orphanedAnswerInstances');

  const dispatch = useDispatch();
  const {
    settings: {
      personalInsurance,
      contactInformationFirst,
      aiAssistedBusinessSelection,
    },
  } = useSettings();
  const isOpenAiUp = useIsOpenAiUp();
  const showAiAssistedSelection =
    aiAssistedBusinessSelection && isOpenAiUp !== false; // logic here is that we want to default to showing it and only hide it once we know there's an issue
  const { settings, tileClasses } = useTileSelectorSettings();
  const params = useParams();
  const [warningInfo, setWarningInfo] = useState({
    open: false,
    message: '',
    isNavBack: false,
  });
  // has a selection been made, even if it is not in state
  const [hasBusinessBeenSelected, setHasBusinessBeenSelected] =
    useState<boolean>(Boolean(selectedBusinessType));

  const [pendingNavType, setPendingNavType] = useState<
    undefined | 'forward' | 'backward'
  >(); // which way are they attempting to navigate?
  const [waitingToNavigate, setWaitingToNavigate] = useState<boolean>(false); // has the user clicked next and now we're waiting for something to finish?
  const [waitingForWarning, setWaitingForWarning] = useState<boolean>(false); // are we waiting for the warning modal when you switch businesses?
  const [blocked, setBlocked] = useState<boolean>(contactInformationFirst); // is the component blocked from navigating? Currently only done for navigation forward.
  const [selectedBusinessTypeId, setSelectedBusinessTypeId] = useState<
    undefined | BusinessType['id']
  >();
  const [onVago, setOnVago] = useState<boolean>(false);
  const businessInputTimerIdRef = useRef<ReturnType<typeof setTimeout>>();
  const gptBusinessTimerIdRef = useRef<ReturnType<typeof setTimeout>>();
  const [getGeneralInformationForm, { error, data }] = useLazyQuery(
    GET_GENERAL_INFORMATION_FORM_FOR_BUSINESS,
  );
  const googleAnalytics = useWalnutGoogleAnalytics();

  const [gptBusinessOptions, setGptBusinessOptions] = useState<
    Array<gptBusinessType>
  >([]);
  const gptBusinessOptionsSelector = useSelector(
    (state: StoreState) => state.quoteWizard.businessTypeData,
  );
  const businessDescrptionSelector = useSelector(
    (state: StoreState) => state.quoteWizard.businessDescription,
  );

  const [
    getBusinessTypeWithGpt,
    {
      error: businessTypeError,
      data: businessTypeData,
      loading: businessTypeLoading,
    },
  ] = useLazyQuery(GET_BUSINESS_TYPE_WITH_GPT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [gptClarifyQuestions, setGptClarifyQuestions] = useState<
    string | undefined
  >('');

  const [businessDescription, setBusinessDescription] = useState<
    string | undefined
  >(undefined);

  const theme = useTheme();
  const tooltipStyles = {
    tooltip: {
      fontSize: '0.9rem',
      backgroundColor: theme.palette.primary.main,
      padding: '8px',
    },
  };
  const CustomTooltip = withStyles(tooltipStyles)(Tooltip);

  const [manualBusinessTypeChecked, setManualBusinessTypeChecked] =
    useState<boolean>(false);
  const manualBusinessTypeCheckedSelector = useSelector(
    (state: StoreState) => state.quoteWizard.businessTypeFoundManually,
  );

  useEffect(() => {
    if (settings === vagoSettings) {
      setOnVago(true);
    }
  }, [settings]);

  // if we're on vago, set the business type
  useEffect(() => {
    if (onVago) {
      const matchingBusinessType = allBusinessTypes.find(
        (businessType) =>
          businessType.internalName === VAGO_BUSINESS_INTERNAL_NAME,
      );
      if (matchingBusinessType) {
        onBusinessSelection(matchingBusinessType);
        setPendingNavType('forward');
        setWaitingToNavigate(true);
        setBlocked(false);
      }
    }
  }, [onVago]);

  // create the statistical sessionId
  useEffect(() => {
    dispatch(requestNewStatisticalSessionThunk());
    dispatch(
      createPageLoadThunk({
        page: 'Business Selection',
        pageNumber: currentStep,
        totalPages: totalSteps,
      }),
    );

    setGptBusinessOptions(gptBusinessOptionsSelector);
    setBusinessDescription(businessDescrptionSelector);
    setManualBusinessTypeChecked(manualBusinessTypeCheckedSelector);
  }, []);

  // hide the save button
  useEffect(() => {
    dispatch(hideSaveButton());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // send a notification about loading into the page
  useEffect(() => {
    if (googleAnalytics) {
      googleAnalytics.triggerEvent(
        GAEventNames.Business,
        EventActions.PageLoad,
        '',
      );
    }
  }, [googleAnalytics]);

  // set the selected business id
  useEffect(() => {
    if (!selectedBusinessType) {
      setSelectedBusinessTypeId(undefined);
    } else if ('id' in selectedBusinessType) {
      setSelectedBusinessTypeId(selectedBusinessType.id);
    } else if ('getMangledId' in selectedBusinessType) {
      setSelectedBusinessTypeId(selectedBusinessType.getMangledId());
    }
  }, [selectedBusinessType]);

  const businessOptions = allBusinessTypes.map((business) => ({
    value: 'getMangledId' in business ? business.getMangledId() : business.id,
    label: business.displayName,
    tileIdentifier: business.internalName,
  }));
  const genericOption = businessOptions.find((option) => {
    return /^generic$/i.test(option.label);
  });
  if (genericOption) {
    businessOptions.push({
      label: BUSINESS_NOT_FOUND_LABEL,
      value: `generic_${genericOption.value}`,
      tileIdentifier: undefined,
    });
  }

  // once we get the form in, put it in the redux store (or just navigate if there's an error)
  useEffect(() => {
    if (error) {
      bugsnag.notify(
        new Error(
          `Error getting general information form. ${JSON.stringify(
            error,
            null,
            4,
          )}`,
        ),
      );
      setBlocked(false);
    } else if (data && data.getGeneralInformationFormForBusiness) {
      const generalInformationForm =
        ProgramBuilderForm.generateFromBackendResponse(
          data.getGeneralInformationFormForBusiness,
        ).toQuoteWizardForm(language);
      dispatch(
        actions.addForms({ forms: generalInformationForm, overwrite: false }),
      );
    }
  }, [contactInformationFirst, error, data, language]);

  // if we are waiting for the general information form to be loaded and then it finished, we are no longer blocked
  useEffect(() => {
    if (
      !contactInformationFirst ||
      (contactInformationFirst && generalInformationFormLoaded)
    ) {
      setBlocked(waitingForWarning);
    }
  }, [
    contactInformationFirst,
    generalInformationFormLoaded,
    waitingForWarning,
  ]);

  // navigate when you can
  useEffect(() => {
    if (waitingToNavigate && !blocked && !waitingForWarning) {
      if (pendingNavType === 'forward') {
        setWaitingToNavigate(false); // so if we navigate backwards we don't immediately go forward again
        nextStep();
      }
    }
  }, [waitingToNavigate, blocked, pendingNavType]);

  useEffect(() => {
    if (businessTypeData && businessTypeData.getBusinessTypeWithGpt) {
      const gptBusinessOptions = businessTypeData.getBusinessTypeWithGpt.map(
        (business) => ({
          value: business?.id,
          label: business?.displayName,
          tileIdentifier: business?.internalName,
        }),
      );

      setGptBusinessOptions(gptBusinessOptions);
    }
  }, [businessTypeData]);

  useEffect(() => {
    // this will run whenever the backend returns an error from the gpt query. the backend will throw an error
    // if gpt wants more information from the user. in that case, we can ask the user for more info

    // this guard had to be set as otherwise this was causing an infinite render loop
    if (businessTypeError?.message !== gptClarifyQuestions) {
      setGptClarifyQuestions(businessTypeError?.message);
    }

    // we also want to set the gpt business options to an empty array
    // but we don't want to touch the redux store, as we don't want to lose the user's business type
    // if they go back and forth between the form

    if (
      businessTypeError &&
      businessTypeError.message &&
      businessTypeError?.message !== gptClarifyQuestions
    ) {
      if (
        businessTypeError.message.includes(
          "We weren't able to determine your business type based on your description",
        )
      ) {
        setGptBusinessOptions([]);
      } else {
        setGptClarifyQuestions(
          'There was an issue determining your business type with AI. Please try again shortly or select your business type manually.',
        );
        setManualBusinessTypeChecked(true);
        dispatch(
          setBusinessTypeFoundManually({ businessTypeFoundManually: true }),
        );
      }
    }
  }, [businessTypeError]);

  /**
   * Saves the selected business in the store state
   * @param values - The values for the fields. In this case, just the businessType
   */
  const handleBusinessSubmission = (values: $TSFixMe) => {
    let selectedBusinessType: BusinessType | BusinessTypeAlias | undefined;
    if (values.businessType) {
      if (/generic_/i.test(values.businessType)) {
        selectedBusinessType = allBusinessTypes.find((business) =>
          /^generic$/i.test(business.displayName),
        );
        setAdditionalInformation(
          "I couldn't find my business in the list provided. I would describe my business as...",
        );
      } else {
        selectedBusinessType = allBusinessTypes.find(
          (selectableBusinessType: ArrayElement<typeof allBusinessTypes>) => {
            const businessId =
              'id' in selectableBusinessType
                ? selectableBusinessType.id
                : selectableBusinessType.getMangledId();
            return values.businessType === businessId;
          },
        );
      }
    }
    if (selectedBusinessType) {
      onBusinessSelection(selectedBusinessType);
    }
  };

  /**
   * Closes business change alert dialog
   */
  const handleClose = () => {
    setWarningInfo((prevState) => ({
      ...prevState,
      open: false,
      isNavBack: false,
    }));
    setWaitingForWarning(false);
  };

  return (
    <Form
      className={classes.form}
      autoComplete='off'
      id='Business'
      noValidate
      onSubmit={(formState) => {
        const { values } = formState;
        if (googleAnalytics) {
          const businessId = String(values.businessType);
          const matchingOption = businessOptions.find(
            (option) => option.value === businessId,
          );
          const description = matchingOption
            ? matchingOption.label
            : 'Unable to determine';
          googleAnalytics.triggerEvent(
            GAEventNames.Business,
            EventActions.Selection,
            description,
          );
        } else if (process.env.NODE_ENV === 'development') {
          //console.log('No Google Analytics!');
        }
        handleBusinessSubmission(values);
      }}
      data-testid='business-selection-wizard-step'
      initialValues={{
        businessType: !selectedBusinessType
          ? undefined
          : 'id' in selectedBusinessType
          ? selectedBusinessType.id
          : selectedBusinessType.getMangledId(),
      }}
    >
      {({ formState, formApi }: { formState: FormState; formApi: FormApi }) => {
        return (
          <>
            <FormLabel htmlFor='business-type'>
              <Typography variant='h5'>
                What is your line of business?
              </Typography>
            </FormLabel>

            <FullWidthCollapse
              in={Boolean(
                showAiAssistedSelection &&
                  manualBusinessTypeChecked &&
                  gptClarifyQuestions != '' &&
                  gptClarifyQuestions &&
                  gptClarifyQuestions.includes(
                    'There was an issue determining your business type with AI. Please try again shortly or select your business type manually.',
                  ),
              )}
            >
              <Grid
                container
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant='body1'>
                  {gptClarifyQuestions?.split('.')[0] + '.'}
                </Typography>
                <CustomTooltip
                  title={
                    gptClarifyQuestions?.split('.').slice(1).join('.') ?? ''
                  }
                  enterDelay={100}
                  leaveDelay={100}
                  placement='right-start'
                >
                  <HelpSharpIcon color='primary' fontSize='small' />
                </CustomTooltip>
              </Grid>
            </FullWidthCollapse>

            <FullWidthCollapse
              in={showAiAssistedSelection && !manualBusinessTypeChecked}
            >
              <FullWidthFormControl>
                <TextField
                  label='Please describe your business'
                  name='businessDescription'
                  variant='standard'
                  multiline
                  minRows={3}
                  value={businessDescription}
                  onChange={(e) => {
                    setBusinessDescription(e.target.value);

                    // fires when the value in the input changes (i.e. they type something)
                    if (gptBusinessTimerIdRef.current) {
                      clearTimeout(gptBusinessTimerIdRef.current);
                    }
                    const timerId = setTimeout(() => {
                      getBusinessTypeWithGpt({
                        variables: {
                          businessDescription: e.target.value,
                        },
                      });
                    }, GPT_BUSINESS_SELECTION_DISPATCH_INTERVAL);
                    gptBusinessTimerIdRef.current = timerId;
                  }}
                  disabled={manualBusinessTypeChecked}
                />
                {businessTypeLoading ? (
                  <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                  >
                    {Array.from({ length: 5 }).map(() => (
                      <Skeleton
                        style={{
                          marginTop: '1rem',
                          width: '63%',
                          minHeight: '3rem',
                          borderRadius: '4px',
                        }}
                        variant='rectangular'
                      />
                    ))}
                  </Grid>
                ) : null}
                {gptClarifyQuestions != '' && gptClarifyQuestions ? (
                  <Grid container justifyContent='space-between'>
                    <Typography variant='body1'>
                      {gptClarifyQuestions?.split('.')[0] + '.'}
                    </Typography>
                    <CustomTooltip
                      title={gptClarifyQuestions?.split('.').slice(1).join('.')}
                      enterDelay={100}
                      leaveDelay={100}
                      placement='right-start'
                    >
                      <HelpSharpIcon color='primary' fontSize='small' />
                    </CustomTooltip>
                  </Grid>
                ) : null}
                {gptBusinessOptions?.length > 0 && !businessTypeLoading ? (
                  <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                  >
                    {gptBusinessOptions.map((option) => (
                      <Fade in={true} timeout={300} key={option.value}>
                        <Button
                          style={{
                            marginTop: '1rem',
                            width: '63%',
                            fontSize: '1.2rem',
                            minHeight: '2rem',
                            textTransform: 'none',
                          }} // 72px is twice the regular height of an mui button
                          disabled={manualBusinessTypeChecked}
                          variant={'contained'}
                          color={
                            selectedBusinessTypeId === option.value
                              ? 'primary'
                              : 'secondary'
                          }
                          onClick={() => {
                            setWaitingToNavigate(true);
                            setPendingNavType('forward');

                            const oldBusinessTypeId = selectedBusinessTypeId;

                            formApi.setValue('businessType', option.value);

                            const selectedBusiness = allBusinessTypes.find(
                              (b: ArrayElement<typeof allBusinessTypes>) => {
                                const businessId =
                                  'id' in b ? b.id : b.getMangledId();
                                return option.value === businessId;
                              },
                            );

                            if (
                              option.value !== oldBusinessTypeId &&
                              oldBusinessTypeId != '' &&
                              oldBusinessTypeId
                            ) {
                              setWaitingForWarning(true);
                              setWarningInfo({
                                open: true,
                                message:
                                  'Selecting a new business type will delete any data entered in the previous business type. This action is irreversible.',
                                isNavBack: false,
                              });
                            } else {
                              setSelectedBusinessTypeId(option.value);

                              if (selectedBusiness) {
                                onBusinessSelection(selectedBusiness);
                              }

                              formApi.submitForm();

                              dispatch(
                                setBusinessTypeData({
                                  businessTypeData: gptBusinessOptions,
                                }),
                              );
                              dispatch(
                                setBusinessDescriptionRedux({
                                  businessDescription: businessDescription,
                                }),
                              );

                              getGeneralInformationForm({
                                variables: {
                                  businessId: option.value,
                                },
                              });
                            }
                          }}
                        >
                          {option.label}
                        </Button>
                      </Fade>
                    ))}
                  </Grid>
                ) : null}
              </FullWidthFormControl>
            </FullWidthCollapse>

            <FullWidthCollapse in={showAiAssistedSelection}>
              <FullWidthFormControl>
                <FormControlLabel
                  style={{ marginTop: '4rem' }}
                  control={
                    <Checkbox
                      checked={manualBusinessTypeChecked}
                      onChange={() => {
                        setManualBusinessTypeChecked((checked) => !checked);
                        dispatch(
                          setBusinessTypeFoundManually({
                            businessTypeFoundManually:
                              !manualBusinessTypeChecked,
                          }),
                        );
                      }}
                    />
                  }
                  label='I would prefer to manually find my business type'
                />
              </FullWidthFormControl>
            </FullWidthCollapse>

            <FullWidthCollapse
              in={!showAiAssistedSelection || manualBusinessTypeChecked}
            >
              <FullWidthFormControl>
                {businessOptions.length <= 20 ? (
                  <TileSelector
                    name='businessType'
                    inputId='business-type'
                    aria-label='Business Type'
                    onChange={() => {
                      setHasBusinessBeenSelected(true);
                      dispatch(
                        createBusinessSelectionInputThunk(
                          // @ts-expect-error
                          allBusinessTypes.find(
                            (business) =>
                              ('id' in business
                                ? business.id
                                : business.original.id) ===
                              formState.values['businessType'],
                          )?.displayName,
                        ),
                      );
                    }}
                    options={businessOptions}
                    settings={settings}
                    tileClasses={tileClasses}
                  />
                ) : (
                  <>
                    <Select
                      name='businessType'
                      inputId='business-type'
                      isDisabled={!businessOptions}
                      isClearable={false}
                      aria-label='Business Type'
                      options={businessOptions}
                      placeholder='Start typing your line of business'
                      validate={(value: $TSFixMe) => {
                        if (!value) {
                          return 'Must select a business or industry to continue';
                        }
                      }}
                      onChange={(value: $TSFixMe) => {
                        setHasBusinessBeenSelected(true);
                        if (value) {
                          setBusinessDescription(value.label);

                          // reset the gpt business options if we selected a business type not present in the gpt options
                          for (let i = 0; i < gptBusinessOptions.length; i++) {
                            if (gptBusinessOptions[i]?.value === value.value) {
                              return;
                            }
                          }

                          dispatch(
                            setBusinessTypeData({ businessTypeData: [] }),
                          );
                          setGptBusinessOptions([]);
                        }
                      }}
                      onInputChange={(
                        _event: React.SyntheticEvent,
                        value: string,
                        reason: string,
                      ) => {
                        // fires when the value in the input changes (i.e. they type something)
                        if (businessInputTimerIdRef.current) {
                          clearTimeout(businessInputTimerIdRef.current);
                        }
                        const timerId = setTimeout(
                          () =>
                            dispatch(createBusinessSelectionInputThunk(value)),
                          BUSINESS_SELECTION_DISPATCH_INTERVAL,
                        );
                        businessInputTimerIdRef.current = timerId;

                        if (reason === 'input') {
                          setBusinessDescription(value);
                        }
                      }}
                      inputValue={
                        businessDescription != undefined
                          ? businessDescription
                          : selectedBusinessType?.displayName
                      }
                      filterOptions={businessOptionsMatcher}
                      isOptionEqualToValue={(
                        option: $TSFixMe,
                        value: $TSFixMe,
                      ) => {
                        return option.value === value.value;
                      }}
                      getOptionLabel={(option: $TSFixMe) => option.label}
                      renderOption={(
                        props: $TSFixMe,
                        option: $TSFixMe,
                        _state: $TSFixMe,
                      ) => {
                        return (
                          <li {...props}>{generateHighlightedLabel(option)}</li>
                        );
                      }}
                    />
                  </>
                )}
              </FullWidthFormControl>
            </FullWidthCollapse>

            <div className={classes.navButtons}>
              {personalInsurance && params?.directTo !== 'commercial' ? (
                <BackButton
                  onClick={() => {
                    if (selectedBusinessType) {
                      setWarningInfo({
                        open: true,
                        message:
                          'Returning to insurance type selection will delete any data entered. This action is irreversible.',
                        isNavBack: true,
                      });
                    } else {
                      dispatch(
                        actions.selectInsurType({
                          selectedInsurType: null,
                        }),
                      );
                    }
                  }}
                />
              ) : (
                <Box />
              )}

              <NextButton
                onClick={() => {
                  setWaitingToNavigate(true);
                  setPendingNavType('forward');
                  const selectedBusinessTypeId = !selectedBusinessType
                    ? undefined
                    : 'id' in selectedBusinessType
                    ? selectedBusinessType.id
                    : selectedBusinessType.getMangledId();
                  if (
                    !selectedBusinessType ||
                    formState.values.businessType === selectedBusinessTypeId ||
                    !manualBusinessTypeChecked
                  ) {
                    formApi.submitForm();
                    dispatch(
                      setBusinessDescriptionRedux({
                        businessDescription: businessDescription,
                      }),
                    );

                    if (
                      contactInformationFirst &&
                      formState.values.businessType &&
                      typeof formState.values.businessType === 'string'
                    ) {
                      let businessId = unmangleId(
                        formState.values.businessType,
                      );
                      getGeneralInformationForm({
                        variables: {
                          businessId,
                        },
                      });
                    }
                  } else {
                    setWaitingForWarning(true);
                    setWarningInfo({
                      open: true,
                      message:
                        'Selecting a new business type will delete any data entered in the previous business type. This action is irreversible.',
                      isNavBack: false,
                    });
                  }
                }}
                disabled={
                  !hasBusinessBeenSelected ||
                  (personalInsurance && !selectedInsurType)
                }
              />
            </div>

            <DataLossWarning
              warningInfo={warningInfo}
              onBack={() => {
                setPendingNavType(undefined);
                formApi.setValue('businessType', selectedBusinessTypeId);
                handleClose();
              }}
              onContinue={() => {
                handleClose();
                if (!warningInfo.isNavBack) {
                  formApi.submitForm();

                  dispatch(
                    setBusinessTypeData({
                      businessTypeData: gptBusinessOptions,
                    }),
                  );
                  dispatch(
                    setBusinessDescriptionRedux({
                      businessDescription: businessDescription,
                    }),
                  );

                  const businessId = formState.values.businessType;
                  // need to get this from the gpt buttons. the business id isn't set i don't think
                  if (businessId && typeof businessId === 'string') {
                    getGeneralInformationForm({
                      variables: {
                        businessId: unmangleId(businessId),
                      },
                    });
                  }
                } else {
                  dispatch(
                    actions.selectInsurType({
                      selectedInsurType: null,
                    }),
                  );
                }
              }}
            />
          </>
        );
      }}
    </Form>
  );
}

/* Styled Components */
const FullWidthCollapse = styled(Collapse)({
  width: '100%',
});

const FullWidthFormControl = styled(FormControl)({
  width: '100%',
});

/* Theming Layer */
const BusinessSelectionDisplayWithStyles = withStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: `${theme.spacing(6)} ${theme.spacing(12)}`,
    margin: 'auto',
    maxWidth: '60rem',
    '& label': {
      alignSelf: 'center',
      marginBottom: theme.spacing(1),
    },
    // Smaller Screens
    [theme.breakpoints.down('lg')]: {
      padding: `${theme.spacing(3)} ${theme.spacing(0)}`,
    },
    [theme.breakpoints.only('md')]: {
      padding: `${theme.spacing(3)} ${theme.spacing(12)}`,
      maxWidth: '50rem',
    },
  },
  navButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    width: '100%',
  },
  formControl: {
    marginBottom: theme.spacing(3),
  },
  // @ts-expect-error
}))((props) => <QuoteBusinessSelectionDisplay {...props} />);

/* GraphQL Layer */
export function QuoteBusinessSelectionQueryLayer({
  children,
  ...mappedState
}: $TSFixMe) {
  const [getAllBusinessTypes, { loading, error, data }] = useLazyQuery(
    allBusinessTypesQuery,
  );
  const {
    settings: { businessFilter, personalInsurance },
  } = useSettings();
  const selectedInsurType = useSelector(
    (state: StoreState) => state.quoteWizard.selectedInsurType,
  );

  // load the business types on initial mount
  useEffect(() => {
    if (
      !personalInsurance ||
      (personalInsurance && selectedInsurType === 'commercial')
    ) {
      getAllBusinessTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || error) {
    return children({ loading, error, getAllBusinessTypes });
  }

  let allBusinessTypes: Array<$TSFixMe> = [];
  if (data && data.allBusinessTypes) {
    allBusinessTypes = data.allBusinessTypes.reduce(
      (
        acc: Array<BusinessType | BusinessTypeAlias>,
        individualBusinessInfo: $TSFixMe,
      ) => {
        const aliases = JSON.parse(individualBusinessInfo.aliases);
        return [
          ...acc,
          individualBusinessInfo,
          ...aliases.map(
            (alias: string) =>
              new BusinessTypeAlias(individualBusinessInfo, alias),
          ),
        ];
      },
      [],
    );
    allBusinessTypes = allBusinessTypes.filter(businessFilter);
  }

  return children({
    allBusinessTypes,
    getAllBusinessTypes,
    ...mappedState,
  });
}

/* Redux Layer */
function mapStateToProps(state: StoreState) {
  const selectedBusinessType = state.quoteWizard.selectedBusinessType;
  const selectedInsurType = state.quoteWizard.selectedInsurType;
  const language = state.quoteWizard.selectedLanguage;
  const generalInformationFormLoaded =
    Object.keys(state.quoteWizard.businessForm).length > 0;

  return {
    selectedBusinessType,
    selectedInsurType,
    language,
    generalInformationFormLoaded,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  const {
    // selectBusinessType,
    setAdditionalInformation: setAdditionalInformationAction,
  } = QuoteWizardState.actions;

  function onBusinessSelection(
    selectedBusinessType: BusinessType | BusinessTypeAlias,
  ) {
    // @ts-expect-error
    dispatch(selectBusinessTypeThunk(selectedBusinessType));
    dispatch(
      // @ts-expect-error
      createBusinessSelectionSelectedThunk(selectedBusinessType.displayName),
    );
  }

  return {
    onBusinessSelection,
    setAdditionalInformation: (additionalInformation: string) =>
      dispatch(setAdditionalInformationAction(additionalInformation)),
  };
}

export const QuoteBusinessSelectionConnectLayer = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default QuoteBusinessSelectionConnectLayer((mappedState) => (
  <QuoteBusinessSelectionQueryLayer {...mappedState}>
    {(props: $TSFixMe) => {
      const { loading, error, getAllBusinessTypes, ...passThroughProps } =
        props;

      const handleDisconnectionError = (error: $TSFixMe) => {
        if (error?.networkError) {
          return (
            <DisconnectionError
              reset={() => {
                getAllBusinessTypes();
              }}
            />
          );
        }
      };

      if (loading) {
        return <LoadingScreen />;
      }

      if (error) {
        return handleErrors(error, handleDisconnectionError);
      }

      return <BusinessSelectionDisplayWithStyles {...passThroughProps} />;
    }}
  </QuoteBusinessSelectionQueryLayer>
));

const GET_BUSINESS_TYPE_WITH_GPT = graphql(`
  query getBusinessTypeWithGpt($businessDescription: String!) {
    getBusinessTypeWithGpt(businessDescription: $businessDescription) {
      id
      displayName
      internalName
    }
  }
`);
